// Localização: frontend/src/components/Financeiro/CollapsibleStatusFilter.jsx
// Componente de Filtro de Status com Dropdown

import React, { useState, useRef, useEffect } from 'react';
import { Tag, ChevronDown, Check } from 'lucide-react';

/**
 * @component CollapsibleStatusFilter
 * @description Componente de filtro de status com dropdown (similar ao Dashboard)
 * @param {Array} status - Array de status selecionados
 * @param {function} onStatusChange - Callback quando status muda
 * @param {string} type - Tipo de conta ('pagar' ou 'receber') para definir status disponíveis
 * @param {string} className - Classes CSS adicionais
 * @param {boolean} showOnlyContent - Se true, mostra apenas o conteúdo do dropdown sem o botão trigger
 */
const CollapsibleStatusFilter = ({
    status = [],
    onStatusChange,
    type = 'pagar',
    className = '',
    showOnlyContent = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    // Definir opções de status baseado no tipo
    const statusOptions = type === 'receber'
        ? [
            { value: 'Pendente', label: 'Pendente', bgColor: 'bg-yellow-50', activeBgColor: 'bg-yellow-100', color: 'text-yellow-600', hoverColor: 'hover:bg-yellow-100' },
            { value: 'Recebido', label: 'Recebido', bgColor: 'bg-emerald-50', activeBgColor: 'bg-emerald-100', color: 'text-emerald-600', hoverColor: 'hover:bg-emerald-100' },
            { value: 'Vencido', label: 'Atrasado', bgColor: 'bg-red-50', activeBgColor: 'bg-red-100', color: 'text-red-600', hoverColor: 'hover:bg-red-100' },
            { value: 'Cancelado', label: 'Cancelado', bgColor: 'bg-gray-50', activeBgColor: 'bg-gray-100', color: 'text-gray-600', hoverColor: 'hover:bg-gray-100' }
        ]
        : [
            { value: 'Pendente', label: 'Pendente', bgColor: 'bg-yellow-50', activeBgColor: 'bg-yellow-100', color: 'text-yellow-600', hoverColor: 'hover:bg-yellow-100' },
            { value: 'Pago', label: 'Pago', bgColor: 'bg-emerald-50', activeBgColor: 'bg-emerald-100', color: 'text-emerald-600', hoverColor: 'hover:bg-emerald-100' },
            { value: 'Vencido', label: 'Atrasado', bgColor: 'bg-red-50', activeBgColor: 'bg-red-100', color: 'text-red-600', hoverColor: 'hover:bg-red-100' },
            { value: 'Cancelado', label: 'Cancelado', bgColor: 'bg-gray-50', activeBgColor: 'bg-gray-100', color: 'text-gray-600', hoverColor: 'hover:bg-gray-100' }
        ];

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current && 
                buttonRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Fechar ao pressionar Escape
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    const handleStatusToggle = (statusValue) => {
        if (!onStatusChange) return;
        
        const currentStatus = status || [];
        let newStatus;
        if (currentStatus.includes(statusValue)) {
            newStatus = currentStatus.filter(s => s !== statusValue);
        } else {
            newStatus = [...currentStatus, statusValue];
        }
        onStatusChange(newStatus);
    };

    const hasActiveFilters = status && status.length > 0;
    const statusLabel = hasActiveFilters 
        ? `${status.length} ${status.length === 1 ? 'status' : 'status'}`
        : 'Status';

    // Função auxiliar para renderizar o conteúdo do dropdown
    const renderDropdownContent = () => {
        return (
            <>
                {statusOptions.map(option => {
                    const isSelected = status?.includes(option.value);
                    return (
                        <button
                            key={option.value}
                            onClick={() => handleStatusToggle(option.value)}
                            className={`
                                w-full flex items-center gap-3 px-4 py-2.5
                                hover:bg-gray-50 transition-colors
                                text-left
                                ${isSelected ? 'bg-emerald-50' : ''}
                            `}
                        >
                            <div className={`
                                flex items-center justify-center w-5 h-5 rounded border-2 transition-colors
                                ${isSelected 
                                    ? 'bg-emerald-600 border-emerald-600' 
                                    : 'border-gray-300 bg-white'
                                }
                            `}>
                                {isSelected && (
                                    <Check className="h-3 w-3 text-white" />
                                )}
                            </div>
                            <span className={`text-sm font-medium ${isSelected ? 'text-emerald-700' : 'text-gray-700'}`}>
                                {option.label}
                            </span>
                        </button>
                    );
                })}
            </>
        );
    };

    // Se showOnlyContent for true, renderizar apenas o conteúdo do dropdown
    if (showOnlyContent) {
        return (
            <div className={`bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-[100] max-h-[calc(100vh-8rem)] sm:max-h-[80vh] overflow-y-auto ${className}`}>
                {renderDropdownContent()}
            </div>
        );
    }

    return (
        <div className={`relative inline-block ${className}`}>
            {/* Botão Trigger */}
            <button
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 border shadow-sm hover:shadow-md ${
                    hasActiveFilters || isOpen
                        ? 'bg-emerald-100 text-emerald-600 border-emerald-300 border-[0.5px] shadow-md font-semibold'
                        : 'bg-emerald-50 text-emerald-600 border-transparent hover:border-emerald-300 hover:bg-emerald-100'
                }`}
            >
                <Tag className="h-3.5 w-3.5" />
                <span>{statusLabel}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    ref={dropdownRef}
                    className="absolute top-full left-0 mt-2 z-[9999] min-w-[200px] bg-white rounded-xl shadow-2xl border border-gray-200 py-2 animate-fadeIn"
                >
                    {renderDropdownContent()}
                </div>
            )}
        </div>
    );
};

export default CollapsibleStatusFilter;

